import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { useAuthState } from "@/data/user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@sphinx/design-system/dropdown-menu";
import { cn } from "@/lib/utils";
import { useHostList } from "@/hooks/useClients";
import { track } from "@/lib/analytics";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { resolveHostLogoByName } from "@/lib/host-logo";

interface HostOverlayBarProps {
  projectId: string;
  previewedHostId: string | null;
  onChangePreviewedHostId: (hostId: string | null) => void;
  onEditHost: (hostId: string) => void;
  onCanvasReplaceHost?: (hostId: string) => void;
}

export function HostOverlayBar({
  projectId,
  previewedHostId,
  onChangePreviewedHostId,
  onEditHost,
  onCanvasReplaceHost,
}: HostOverlayBarProps) {
  const { isAuthenticated } = useAuthState();
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const { hosts, isLoading } = useHostList({ isAuthenticated, projectId });
  const [menuOpen, setMenuOpen] = useState(false);

  const validPreviewedHostId =
    previewedHostId && hosts.some((h) => h.hostId === previewedHostId)
      ? previewedHostId
      : null;
  // Fixed target matrix, in canonical order (see data/targets.ts). Fallback:
  // previewed → first enabled target → first target.
  const sortedHosts = hosts;
  const effectiveHostId =
    validPreviewedHostId ??
    sortedHosts.find((h) => h.enabled)?.hostId ??
    sortedHosts[0]?.hostId ??
    null;

  useEffect(() => {
    if (isLoading) return;
    if (effectiveHostId == null) return;
    if (previewedHostId === effectiveHostId) return;
    onChangePreviewedHostId(effectiveHostId);
    // When the active host is deleted out from under us (or reconciled
    // away for any other reason) the canvas is still pointing at the
    // dead id. handleChange would call this directly; the non-interactive
    // path needs the same notification or App.hostsTabSelectedHostId
    // stays stale and "Save" targets a deleted id.
    onCanvasReplaceHost?.(effectiveHostId);
  }, [
    isLoading,
    effectiveHostId,
    previewedHostId,
    onChangePreviewedHostId,
    onCanvasReplaceHost,
  ]);

  const activeIndex = useMemo(
    () =>
      effectiveHostId == null
        ? -1
        : sortedHosts.findIndex((h) => h.hostId === effectiveHostId),
    [sortedHosts, effectiveHostId]
  );

  const effectiveHost = activeIndex >= 0 ? sortedHosts[activeIndex] : null;

  const hasFiredOpened = useRef(false);
  useEffect(() => {
    if (hasFiredOpened.current) return;
    hasFiredOpened.current = true;
    track("connect_host_overlay_opened", {
      location: "scenario_overlay",
      host_count: hosts.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (next: string) => {
    if (next === effectiveHostId) return;
    track("connect_host_overlay_swapped", {
      location: "scenario_overlay",
      from: effectiveHostId ?? "__unknown__",
      to: next,
      host_count: hosts.length,
    });
    onChangePreviewedHostId(next);
    onCanvasReplaceHost?.(next);
  };

  // Cycle to the prev/next host. Wraps around so the arrows never dead-end —
  // with 2 hosts this turns into a fast toggle.
  const cycle = (direction: 1 | -1) => {
    if (sortedHosts.length <= 1 || activeIndex < 0) return;
    const nextIndex =
      (activeIndex + direction + sortedHosts.length) % sortedHosts.length;
    handleChange(sortedHosts[nextIndex].hostId);
  };

  const canCycle = sortedHosts.length > 1;
  const arrowDisabled = isLoading || !canCycle;

  return (
    <div
      className="flex min-w-0 flex-wrap items-center gap-1"
      data-testid="host-overlay-bar"
    >
      {isLoading || !effectiveHost ? (
        <div className="h-8 w-44 animate-pulse rounded-md bg-muted/50" />
      ) : (
        <div className="flex items-center rounded-md border border-border/40 bg-muted/30">
          <button
            type="button"
            aria-label="Previous client"
            data-testid="host-overlay-prev"
            disabled={arrowDisabled}
            onClick={() => cycle(-1)}
            className={cn(
              "inline-flex h-8 w-7 items-center justify-center rounded-l-md text-muted-foreground transition-colors",
              "hover:bg-muted/60 hover:text-foreground",
              "focus-visible:ring-2 focus-visible:ring-ring/45 focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-40"
            )}
          >
            <ChevronLeft className="size-4" />
          </button>

          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Client used for preview"
                data-testid="host-overlay-current"
                className={cn(
                  "flex h-8 min-w-[7rem] max-w-[14rem] items-center justify-center border-x border-border/40 bg-transparent px-3 text-sm font-medium text-foreground transition-colors outline-none",
                  "hover:bg-muted/60 data-[state=open]:bg-muted/60",
                  "focus-visible:ring-2 focus-visible:ring-ring/45"
                )}
              >
                <span className="truncate">{effectiveHost.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
            >
              <DropdownMenuRadioGroup
                value={effectiveHostId ?? undefined}
                onValueChange={handleChange}
              >
                {sortedHosts.map((host) => {
                  const rowLogo = resolveHostLogoByName(host.name, themeMode);
                  return (
                    <DropdownMenuRadioItem
                      key={host.hostId}
                      value={host.hostId}
                      hideIndicator
                      className="group pr-1.5"
                    >
                      <img
                        src={rowLogo}
                        alt=""
                        data-testid={`host-overlay-logo-${host.hostId}`}
                        className="size-3.5 shrink-0 object-contain"
                      />
                      <span
                        className="flex-1 truncate"
                        data-testid={`host-overlay-label-${host.hostId}`}
                      >
                        {host.name}
                      </span>
                      <span className="ml-2 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 group-data-[highlighted]:opacity-100">
                        <button
                          type="button"
                          aria-label={`Edit ${host.name}`}
                          data-testid={`host-overlay-edit-${host.hostId}`}
                          className="inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(false);
                            onEditHost(host.hostId);
                          }}
                        >
                          <Pencil className="size-3.5" />
                        </button>
                      </span>
                      {host.hostId === effectiveHostId ? (
                        <span
                          aria-hidden
                          data-testid={`host-overlay-selected-dot-${host.hostId}`}
                          className="size-1.5 shrink-0 rounded-full bg-primary"
                        />
                      ) : null}
                    </DropdownMenuRadioItem>
                  );
                })}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            aria-label="Next client"
            data-testid="host-overlay-next"
            disabled={arrowDisabled}
            onClick={() => cycle(1)}
            className={cn(
              "inline-flex h-8 w-7 items-center justify-center rounded-r-md text-muted-foreground transition-colors",
              "hover:bg-muted/60 hover:text-foreground",
              "focus-visible:ring-2 focus-visible:ring-ring/45 focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-40"
            )}
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}

    </div>
  );
}
